import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { AiFillPlusCircle } from "react-icons/ai";
import { FaMicrophone } from "react-icons/fa";
import { FaArrowUp } from "react-icons/fa6";
import { LuDelete } from "react-icons/lu";
import { MdEmojiEmotions } from "react-icons/md";
import {ImSearch} from 'react-icons/im'
import { LuClock9 } from "react-icons/lu";
import { BsEmojiGrin } from "react-icons/bs";
import { PiDogDuotone } from "react-icons/pi";
import { LiaAppleAltSolid } from "react-icons/lia";
import { GiSoccerBall } from "react-icons/gi";
import { LiaCarSideSolid } from "react-icons/lia";
import { TfiLightBulb } from "react-icons/tfi";
import { CiHeart } from "react-icons/ci";
import { CiFlag1 } from "react-icons/ci";

import Key from "./Key";
import Emoji from "./Emoji";
import "./WriteMessage.css";

export default function WritePost({ CTA }) {
  const [typedText, setTypedText] = useState("");
  const [pressedKey, setPressedKey] = useState(null);
  const [numKeyPressed, setNumKeyPressed] = useState(null);
  // const [spacebarPressed, setSpacebarPressed] = useState(null);
  const [shiftKeyPressed, setShiftKeyPressed] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  // const [showEmojiKeyboard, setShowEmojiKeyboard] = useState(false);
  const keyBoardState = useRef("letters");
  const isPaused = useRef(false);

  const topUppercaseKeys = ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"];
  const middleUppercaseKeys = ["A", "S", "D", "F", "G", "H", "J", "K", "L"];
  const bottomUppercaseKeys = ["Z", "X", "C", "V", "B", "N", "M"];

  const topLowercaseKeys = ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"];
  const middleLowercaseKeys = ["a", "s", "d", "f", "g", "h", "j", "k", "l"];
  const bottomLowercaseKeys = ["z", "x", "c", "v", "b", "n", "m"];

  const topNumberKeys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
  const middleNumberKeys = ["-", "/", ":", ";", "(", ")", "$", "&", "@", '"'];
  const bottomNumberKeys = [".", ",", "?", "!", `'`];

  const topEmojiKeys = [
    "\u{1F60A}", // 😊
    "\u{1F923}", // 🤣
    "\u{1F62E}\u{200D}\u{1F4A8}", // 😮‍💨 (Multi-codepoint emoji)
    "\u{1F970}", // 🥰
    "\u{1F60D}", // 😍
    "\u{1F929}", // 🤩
    "\u{1F6E9}", // 🛩️
  ];
  const middleTopEmojiKeys = [
    "\u{1F62D}",
    "\u{1F3AF}",
    "\u{1F4AF}",
    "\u{1F44C}",
    "\u{1F44D}",
    "\u{1FAE0}",
    "\u{1F495}",
  ]; // 😭 🎯 💯 👌 👍 🫠 💕
  const middleBottomEmojiKeys = [
    "\u{2764}\u{FE0F}\u{200D}\u{1F525}",
    "\u{1F63B}",
    "\u{1F381}",
    "\u{1F973}",
    "\u{1F942}",
    "\u{1F93E}\u{200D}\u{2642}\u{FE0F}",
    "\u{1F351}",
  ]; // ❤️‍🔥 😻 🎁 🥳 🥂 🤾‍♂️ 🍑
  const bottomEmojiKeys = [
    "\u{1F4B8}",
    "\u{1F440}",
    "\u{1F91D}",
    "\u{1F648}",
    "\u{1F633}",
    "\u{1F630}",
    "\u{1F4A6}",
  ]; // 💸 👀 🤝 🙈 😳 😰 💦

  const [topRow, setTopRow] = useState(topUppercaseKeys);
  const [middleRow, setMiddleRow] = useState(middleUppercaseKeys);
  const [bottomRow, setBottomRow] = useState(bottomUppercaseKeys);

  // prevents emojis from splitting
  const characters = Array.from(CTA);
  const typingSpeed = 100; // Adjust speed here
  const keyHighlightDuration = 80; // Ensures key highlight animation plays fully
  const idleTimeout = 1000;

  const handleKeyPress = async (letter, index) => {
    if(isPaused.current) return;
    setPressedKey(null);

    if (index === 1) {
      setShiftKeyPressed(false);
    }

    // Determine keyboard type
    const isEmoji =
      topEmojiKeys.includes(letter) ||
      middleTopEmojiKeys.includes(letter) ||
      middleBottomEmojiKeys.includes(letter) ||
      bottomEmojiKeys.includes(letter);
    const isNumber =
      topNumberKeys.includes(letter) ||
      middleNumberKeys.includes(letter) ||
      bottomNumberKeys.includes(letter);
    const isUppercase =
      topUppercaseKeys.includes(letter) ||
      middleUppercaseKeys.includes(letter) ||
      bottomUppercaseKeys.includes(letter);
    const isLowercase =
      topLowercaseKeys.includes(letter) ||
      middleLowercaseKeys.includes(letter) ||
      bottomLowercaseKeys.includes(letter);
    const isSpecialKey = letter === " " || letter === "\n";

    // Handle Keyboard Switching
    if (isEmoji) {
        if (keyBoardState.current !== "emojis") {
            await new Promise((resolve) => {
                setTimeout(() => {
                    keyBoardState.current = "emojis";
                    resolve();
                }, 100); // Short delay for keyboard to render
            });
        }
        isPaused.current = true; // Pause the sequence

        // wait for emoji to be highlighted
        await new Promise((resolve) => {
            setTimeout(() => {
                setPressedKey(letter);
                resolve();
            }, 500);
        });

        setTypedText((prevText) => prevText + letter);

        setTimeout(() => {
            setPressedKey(null);
            isPaused.current = false;
        }, keyHighlightDuration);
        
        return;
    
    } else if (isNumber) {
      if (keyBoardState.current !== "numbers") {
        keyBoardState.current = "numbers";
        setNumKeyPressed(true);
        setTopRow([...topNumberKeys]);
        setMiddleRow([...middleNumberKeys]);
        setBottomRow([...bottomNumberKeys]);

        setTimeout(() => {
          setNumKeyPressed(false);
        }, keyHighlightDuration);
      }
    } else if (isUppercase) {
      if (keyBoardState.current !== "uppercase") {
        keyBoardState.current = "uppercase";
        setTopRow([...topUppercaseKeys]);
        setMiddleRow([...middleUppercaseKeys]);
        setBottomRow([...bottomUppercaseKeys]);
      }
    } else if (isLowercase || isSpecialKey) {
      if (keyBoardState.current !== "lowercase") {
        keyBoardState.current = "lowercase";
        setTopRow([...topLowercaseKeys]);
        setMiddleRow([...middleLowercaseKeys]);
        setBottomRow([...bottomLowercaseKeys]);
      }
    }

    setPressedKey(letter);
    await new Promise((resolve) => setTimeout(resolve, keyHighlightDuration));
    setPressedKey(null);
    setTypedText((prevText) => prevText + letter);
  };

  useEffect(() => {
    if (!CTA) return;

    setTypedText("");
    setIsTyping(true);
    let index = 0;

    const textInterval = setInterval(() => {
      if (index >= characters.length) {
        clearInterval(textInterval);
        setTimeout(() => setIsTyping(false), idleTimeout);
        return;
      }

      let letter = characters[index];

      if(!isPaused.current){
          handleKeyPress(letter, index);
          index++;
      }
    }, typingSpeed); // This defines the typing speed

    return () => clearInterval(textInterval); // Cleanup when unmounting
  }, [CTA]); // Runs whenever `CTA` updates

  return (
    <div className="message-container">
      <div className="message-keyboard-container">
        <div className="message-top-row-container">
          <div className="plus-icon-container">
            <AiFillPlusCircle className="plus-icon" />
          </div>
          <div className="input-bar">
            <p className="message-text">
              {typedText}
              <span className={`cursor ${isTyping ? "typing" : ""}`}>|</span>
            </p>
            <div className="send-or-mic-container">
              <FaArrowUp className="send-icon" />
            </div>
          </div>
        </div>
        <div className="keyboard-container">

        {keyBoardState.current !== "emojis" ? (
          <>
          <div className="non-emoji-keyboard-container">
              <div className="keyboard-top-row">
                {topRow.map((char, index) => (
                  <Key
                    char={char}
                    key={index}
                    isPressed={pressedKey === char}
                  ></Key>
                ))}
              </div>
              <div className="keyboard-middle-row">
                {middleRow.map((char, index) => (
                  <Key
                    char={char}
                    key={index}
                    isPressed={pressedKey === char}
                  ></Key>
                ))}
              </div>
              <div className="keyboard-bottom-row">
                <Key
                  size="medium"
                  icon={true}
                  isPressed={shiftKeyPressed}
                ></Key>

                {bottomRow.map((char, index) => (
                  <Key
                    char={char}
                    key={index}
                    isPressed={pressedKey === char}
                  ></Key>
                ))}
                <div className="delete-key">
                  <LuDelete className="backspace-icon" />
                </div>
              </div>
              <div className="keyboard-spacebar-row">
                <Key
                  char={numKeyPressed ? "ABC" : "123"}
                  isPressed={numKeyPressed}
                  size="large"
                ></Key>
                <Key
                  char="space"
                  isPressed={pressedKey === " "}
                  size="xl"
                ></Key>
                <Key
                  char="return"
                  isPressed={pressedKey === "\n"}
                  size="large"
                ></Key>
              </div>
          </div>
          </>
        ) : (
          <>
              <div className="all-emojis-container">
                <div className="emoji-search-bar">
                    <ImSearch className="emoji-search-icon"></ImSearch>
                    <p className="search-emoji-p">Search Emoji</p>
                </div>
                <div className="emoji-row">
                  {topEmojiKeys.map((emoji, index) => (
                    <Emoji
                      emoji={emoji}
                      key={`top-${index}`}
                      isPressed={pressedKey === emoji}
                    />
                  ))}
                </div>
                <div className="emoji-row">
                  {middleTopEmojiKeys.map((emoji, index) => (
                    <Emoji
                      emoji={emoji}
                      key={`middle-top-${index}`}
                      isPressed={pressedKey === emoji}
                    />
                  ))}
                </div>
                <div className="emoji-row">
                  {middleBottomEmojiKeys.map((emoji, index) => (
                    <Emoji
                      emoji={emoji}
                      key={`middle-bottom-${index}`}
                      isPressed={pressedKey === emoji}
                    />
                  ))}
                </div>
                <div className="emoji-row">
                  {bottomEmojiKeys.map((emoji, index) => (
                    <Emoji
                      emoji={emoji}
                      key={`bottom-${index}`}
                      isPressed={pressedKey === emoji}
                    />
                  ))}
                </div>
                <div className="mini-find-emoji-container">
                    <LuClock9 className="active-mini-emoji"/>
                    <BsEmojiGrin/>
                    <PiDogDuotone/>
                    <LiaAppleAltSolid/>
                    <GiSoccerBall/>
                    <LiaCarSideSolid/>
                    <TfiLightBulb/>
                    <CiHeart/>
                    <CiFlag1/>
                    <LuDelete className="find-emoji-backspace-icon" />
                </div>
              </div>
          </>
        )}
        <div className="emoji-row-container">
          {keyBoardState.current === "emojis" ? (
              <p className="emoji-or-num-key">ABC</p>
            ) : (
                <MdEmojiEmotions className="emoji-key" />
            )}
          <FaMicrophone className="bottom-mic-icon"></FaMicrophone>
            </div>
        </div>
      </div>
    </div>
  );
}

WritePost.propTypes = {
  details: PropTypes.object,
  index: PropTypes.number,
  template: PropTypes.string,
  CTA: PropTypes.string,
};
